import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import { FormikInput, Button } from "../../UI";
import {
  NewTealiumIntegrationFormSchema,
} from "../../../schemas";
import { useUserContext } from "../../../contexts/UserContext";
import {
  getSpecificTealiumProduct,
  updateSpecificTealiumProduct,
} from "../../../utils/APIs";
import Loader from "../../UI/Loader/Loader";
import { permission } from "../../../config/permission";
import backwardArrow from '../../../assets/icons/backward-arrow.svg';
import { toast } from 'react-toastify';
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import { Eye, EyeOff } from 'lucide-react';

const EditTealiumIntegration = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState();
  const { userData, checkPermission, updateTealiumIntegrations } = useUserContext();
  const { content } = useContentfulContext();

  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showTealiumKey, setShowTealiumKey] = useState(false);
  const disableFieldWithPermission = !checkPermission(permission.EDIT_TEALIUM);

  const getProductData = async () => {
    try {
      setLoading(true);
      if (id && userData?.selectedOrganization?.org_token) {
        const result = await getSpecificTealiumProduct(
          userData.token,
          userData.selectedOrganization.org_token,
          id
        );
        setData(result?.data);
      }
    } catch (error) {
      console.log("Error fetching product data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductData();
  }, [userData, id]);

  const validateFieldChanges = (fieldValues) => {
    if(data?.tealium_account == fieldValues?.tealium_account && 
      data?.tealium_api_name == fieldValues?.tealium_api_name && 
      data?.tealium_key == fieldValues?.tealium_key && 
      data?.tealium_profile == fieldValues?.tealium_profile && 
      data?.tealium_user == fieldValues?.tealium_user
    ) {
      return true;
    }
    return false;
  }

  const handleSave = async (values) => {
    try {

      const rawBodyData = {
        tealium_api_name: values.tealium_api_name,
        tealium_account: values.tealium_account,
        tealium_profile: values.tealium_profile,
        tealium_user: values.tealium_user,
        tealium_key: values.tealium_key,
      };
      
      if(validateFieldChanges(rawBodyData)) return;

      const bodyData = rawBodyData;

      setLoading(true);
      await updateSpecificTealiumProduct(
        userData.token,
        userData.selectedOrganization.org_token,
        { productId: id, bodyData }
      );

      !loading && toast.success(content.EDIT_INTEGRATION_CONTENT.success);
      updateTealiumIntegrations()
      navigate("/setup/tealium-api-key-integrations");
    } catch (error) {
      setShowError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 md:px-8 my-6">
      {loading && <Loader />}
      {data && (
        <div className="w-full lg:w-1/2 space-y-4 md:space-y-6">
            <p className="font-medium cursor-pointer text-primary hover:underline flex items-center" onClick={(e) => {
              e.preventDefault();
              navigate('/setup/tealium-api-key-integrations')
            }}>
              <img src={backwardArrow} alt='' className='w-5 h-5 mr-2' /> {content.EDIT_TEALIUM_INTEGRATION_CONTENT.back_cta_text} 
            </p>
            <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">
              {content.EDIT_TEALIUM_INTEGRATION_CONTENT.title}
            </h2>
            <p>{content.EDIT_TEALIUM_INTEGRATION_CONTENT.description}</p>
            <Formik
              enableReinitialize
              initialValues={{
                tealium_account: data?.tealium_account || "",
                tealium_api_name: data?.tealium_api_name || "",
                tealium_key: data?.tealium_key || "",
                tealium_profile: data?.tealium_profile || "",
                tealium_user: data?.tealium_user || "",
              }}
              validationSchema={() => NewTealiumIntegrationFormSchema(content.NEW_TEALIUM_INTEGRATION_CONTENT)}
              onSubmit={handleSave}    
            >
              {() => (
                <Form action="#" className="space-y-4">
                  <FormikInput
                    type="text"
                    disabled={disableFieldWithPermission}
                    id="tealium_api_name"
                    name="tealium_api_name"
                    label={content.EDIT_TEALIUM_INTEGRATION_CONTENT.formLabels.tealium_api_name}
                  />
                  <FormikInput
                    type="text"
                    disabled={disableFieldWithPermission}
                    id="tealium_account"
                    name="tealium_account"
                    label={content.EDIT_TEALIUM_INTEGRATION_CONTENT.formLabels.tealium_account}
                  />
                  <FormikInput
                    type={showTealiumKey ? "text" : "password"}
                    disabled={disableFieldWithPermission}
                    id="tealium_key"
                    name="tealium_key"
                    label={content.EDIT_TEALIUM_INTEGRATION_CONTENT.formLabels.tealium_key}
                    suffixIcon={
                      <button
                        type="button"
                        onClick={() => setShowTealiumKey(prev => !prev)}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label={showTealiumKey ? "Hide Tealium key" : "Show Tealium key"}
                      >
                        {showTealiumKey ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />
                  <FormikInput
                    type="text"
                    disabled={disableFieldWithPermission}
                    id="tealium_profile"
                    name="tealium_profile"
                    label={content.EDIT_TEALIUM_INTEGRATION_CONTENT.formLabels.tealium_profile}
                  />
                  <FormikInput
                    type="text"
                    disabled={disableFieldWithPermission}
                    id="tealium_user"
                    name="tealium_user"
                    label={content.EDIT_TEALIUM_INTEGRATION_CONTENT.formLabels.tealium_user}
                  />
                  {showError && (
                    <p className="mt-2 text-sm text-red-5">{showError}</p>
                  )}
                  {checkPermission(permission.EDIT_TEALIUM) && (
                    <div className="pt-2">
                      <Button type="submit" variant="primary" className="w-full md:w-auto">
                        {content.EDIT_INTEGRATION_CONTENT.submit_cta}
                      </Button>
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
      )}
    </div>
  );
};

export default EditTealiumIntegration;
